import React from 'react';
import { Alert, Card, Descriptions, Empty, Space, Table, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import type { DailyEngagementConfigResponse } from '@/types';
import {
  CURRENCY_LABELS,
  TIER_LABELS,
  formatValue,
  type ConfigChange,
} from './dailyEngagementConfig';
import { useDraft, type SwitchState } from './draftContext';

const { Paragraph, Text } = Typography;

interface Props {
  served: DailyEngagementConfigResponse;
  switchState: SwitchState;
  changes: ConfigChange[];
}

const MAX_CHANGES_SHOWN = 200;

/** Kill switch, last update, unsaved changes and a short summary of what is served. */
export const OverviewTab: React.FC<Props> = ({ served, switchState, changes }) => {
  const { errors } = useDraft();
  const live = served.dailyEngagementConfig;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="Status">
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Daily Rewards switch">
            <Space wrap>
              {switchState.status === 'loading' && <Tag>Loading…</Tag>}
              {switchState.status === 'error' && <Tag color="default">Couldn&apos;t read it</Tag>}
              {switchState.status === 'ok' &&
                (switchState.on ? (
                  <Tag color="green">ON: players see Daily Rewards</Tag>
                ) : (
                  <Tag color="red">
                    OFF: every Daily Rewards call answers 403; the game hides it
                  </Tag>
                ))}
              <Text type="secondary">
                Flag <Text code>daily_engagement_enabled</Text>; change it on{' '}
                <Link to="/config?tab=flags">Configuration → Feature Flags</Link>.
              </Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Last updated">
            {served.updatedAt ? new Date(served.updatedAt).toLocaleString() : 'Never'}
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                The timestamp of the shared config row: other settings stored on it (force update,
                Blitz, ads) move it too. The audit log has the Daily Rewards history (target{' '}
                <Text code>daily_engagement_config</Text>).
              </Text>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Served now">
            <Space direction="vertical" size={2}>
              <Text>
                Caps per claim:{' '}
                {Object.entries(live.caps)
                  .map(([c, v]) => `${v} ${CURRENCY_LABELS[c as keyof typeof CURRENCY_LABELS]}`)
                  .join(', ')}
              </Text>
              <Text>
                Day 28 card:{' '}
                {live.day28CardId === null || live.day28CardId === undefined
                  ? 'a random Legendary'
                  : `a Legendary of playerIndex ${live.day28CardId}`}
              </Text>
              <Text>
                Spin: {live.spin.bands.length} band{live.spin.bands.length === 1 ? '' : 's'},
                guaranteed first spin after {live.spin.guaranteeAfterMisses} Card Chance days in a
                row with No Card
              </Text>
              <Text>Coin bonus: {live.coinBonusPercent}%</Text>
              <Text>
                Freezes: hold {live.freezes.baseCapacity} (before loyalty), cost{' '}
                {live.freezes.purchaseCostMatchTokens} Match Tokens, {live.freezes.freeFreezes} free
              </Text>
              <Text>
                Loyalty:{' '}
                {live.loyalty.tiers
                  .map((t) => `${TIER_LABELS[t.tier] ?? t.tier} ${t.threshold}`)
                  .join(', ')}{' '}
                claim days
              </Text>
            </Space>
          </Descriptions.Item>
        </Descriptions>
        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          A save reaches every server within seconds and applies to the next read, claim, spin or
          vault opening. Past claims, spins and opened vaults keep what they granted. If the stored
          document were invalid, the server would serve the GDD defaults and this page can&apos;t
          tell: if the values above look like the defaults when they shouldn&apos;t, check the audit
          log (target <Text code>daily_engagement_config</Text>) before saving over it.
        </Paragraph>
      </Card>

      <Card
        size="small"
        title={
          <Space>
            Unsaved changes
            {changes.length > 0 && <Tag color="gold">{changes.length}</Tag>}
          </Space>
        }
      >
        {changes.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No unsaved changes" />
        ) : (
          <Table
            size="small"
            rowKey="path"
            pagination={changes.length > 20 ? { pageSize: 20 } : false}
            dataSource={changes.slice(0, MAX_CHANGES_SHOWN)}
            footer={
              changes.length > MAX_CHANGES_SHOWN
                ? () => (
                    <Text type="secondary">
                      Showing the first {MAX_CHANGES_SHOWN} of {changes.length} changes.
                    </Text>
                  )
                : undefined
            }
            columns={[
              { title: 'Field', dataIndex: 'path', render: (p: string) => <Text code>{p}</Text> },
              { title: 'Saved', render: (_v, c) => formatValue(c.before) },
              { title: 'Unsaved', render: (_v, c) => <Text strong>{formatValue(c.after)}</Text> },
            ]}
          />
        )}
      </Card>

      {errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${errors.length} problem${errors.length === 1 ? '' : 's'} to fix before saving`}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          }
        />
      )}
      {errors.length === 0 && changes.length > 0 && (
        <Alert
          type="success"
          showIcon
          message="The changes pass the same checks the server runs. Save to apply them."
        />
      )}
    </Space>
  );
};
